import React, { Suspense, useEffect, useContext } from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider, Outlet, useParams, useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { Box, CircularProgress, Typography } from '@mui/material';

import { Root } from "./components/root";
import { NotFound } from './components/not-found';
import { GlobalProvider, GlobalContext } from './contexts/global-context';
import { CombinedAuthProvider } from './contexts/auth-context';
import { AuthStateProvider } from './contexts/auth-state-context';
import { AuthOperationsProvider } from './contexts/auth-operations-context';
import { ConfigProvider } from './contexts/config-context';
import { OnboardingProvider } from './contexts/onboarding-context';
import { RequireAuth } from './components/require-auth';
import { ApiProvider } from './services/api';
import useApi from './services/api';
import { ModelProvider } from './contexts/model-context';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { RouteErrorPage } from './components/route-error';
import { AuthRedirector } from './components/auth-redirector';
import { ConfigLoader } from './components/config-loader';
import { HelmetProvider } from 'react-helmet-async';
import ReactGA from 'react-ga4';
import reportWebVitals from './reportWebVitals';

//mui
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

import { AppTheme } from './components/theme';
import { ModelPageSkeleton } from "./components/model-page-skeleton";
import { PlansPageSkeleton } from "./components/plans-page-skeleton";
import { PageLoading } from './components/standard-loading';

// Lazy-loaded route components
import { Home } from "./components/home";
const Models = React.lazy(() => import("./components/models").then(m => ({ default: m.Models })));
const Model = React.lazy(() => import("./components/model"));
const Library = React.lazy(() => import('./components/library'));
const UserSetting = React.lazy(() => import('./components/user-settings').then(m => ({ default: m.UserSetting })));
const OAuthCallback = React.lazy(() => import('./components/oauth-callback').then(m => ({ default: m.OAuthCallback })));
const Contact = React.lazy(() => import('./components/contact').then(m => ({ default: m.Contact })));
const FAQ = React.lazy(() => import('./components/faq').then(m => ({ default: m.FAQ })));
const PrivacyPolicy = React.lazy(() => import('./components/privacy-policy').then(m => ({ default: m.PrivacyPolicy })));
const DataDeletion = React.lazy(() => import('./components/data-deletion').then(m => ({ default: m.DataDeletion })));
const CheckoutSuccess = React.lazy(() => import('./components/checkout-success').then(m => ({ default: m.CheckoutSuccess })));
const CheckoutCancel = React.lazy(() => import('./components/checkout-cancel').then(m => ({ default: m.CheckoutCancel })));

// Component to handle old /model/:id URLs and redirect to /designs/:id
function ModelRedirect() {
  const { id } = useParams();
  return <Navigate to={`/designs/${id}`} replace />;
}

// Simple component to handle password reset URLs and redirect to modal
function PasswordResetHandler() {
  const { uid, token } = useParams();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (uid && token) {
      // Redirect to home with password reset parameters
      navigate(`/?password-reset&uid=${uid}&token=${token}`, { replace: true });
    } else {
      // Invalid URL, redirect to home
      navigate('/', { replace: true });
    }
  }, [uid, token, navigate]);
  
  return null; // This component just handles the redirect
}

// Component to handle login URLs directly
function LoginHandler() {
  const navigate = useNavigate();
  const { setAuthModalType, setShowAuthModal } = useContext(GlobalContext);
  
  useEffect(() => {
    setAuthModalType("login");
    setShowAuthModal(true);
    navigate('/', { replace: true });
  }, [navigate, setAuthModalType, setShowAuthModal]);
  
  return null;
}

// Component to handle signup URLs directly
function SignupHandler() {
  const navigate = useNavigate();
  const { setAuthModalType, setShowAuthModal } = useContext(GlobalContext);
  
  useEffect(() => {
    setAuthModalType("register");
    setShowAuthModal(true);
    navigate('/', { replace: true });
  }, [navigate, setAuthModalType, setShowAuthModal]);
  
  return null;
}

// Component to handle password reset URLs directly
function PasswordResetFormHandler() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuthModalType, setShowAuthModal } = useContext(GlobalContext);
  
  useEffect(() => {
    const token = searchParams.get('token');
    const key = searchParams.get('key');
    
    if (token || key) {
      // Password reset with token/key - redirect to home with parameters
      const resetToken = token || key;
      navigate(`/?password-reset&key=${resetToken}`, { replace: true });
    } else {
      // Password reset request form (no parameters)
      setAuthModalType("request-password-reset");
      setShowAuthModal(true);
      navigate('/', { replace: true });
    }
  }, [searchParams, navigate, setAuthModalType, setShowAuthModal]);
  
  return null;
}

// Component to handle email confirmation success
function EmailConfirmedHandler() {
  const navigate = useNavigate();
  const { setLoginMessage, setAuthModalType, setShowAuthModal } = useContext(GlobalContext);
  
  useEffect(() => {
    setLoginMessage("Email confirmed successfully! You can now log in.");
    setAuthModalType("login");
    setShowAuthModal(true);
    navigate('/', { replace: true });
  }, [navigate, setLoginMessage, setAuthModalType, setShowAuthModal]);
  
  return null;
}

// Component to handle email verification URLs directly
function EmailVerificationHandler() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { confirmEmail } = useApi();
  const { setLoginMessage, setAuthModalType, setShowAuthModal } = useContext(GlobalContext);
  const [loading, setLoading] = React.useState(true);
  const [processed, setProcessed] = React.useState(false);
  
  useEffect(() => {
    const handleEmailVerification = async () => {
      if (processed) return;
      
      const key = searchParams.get('key');
      if (!key) {
        navigate('/', { replace: true });
        return;
      }

      setProcessed(true);
      setLoading(true);

      try {
        await confirmEmail(key);
        // Success - show login modal with success message
        setLoginMessage("Email confirmed successfully! You can now log in.");
        setAuthModalType("login");
        setShowAuthModal(true);
        navigate('/', { replace: true });
      } catch (error) {
        console.error('Email verification response:', error);
        
        // For allauth headless, 401 means email verification succeeded but user not logged in
        if (error?.response?.status === 401) {
          // Email verification successful - show login modal with success message
          setLoginMessage("Email confirmed successfully! You can now log in.");
          setAuthModalType("login");
          setShowAuthModal(true);
          navigate('/', { replace: true });
        } else {
          // Actual error (400, 404, etc.) - show error message
          let errorMessage = 'Email verification failed. Please try again or contact support.';
          
          // Handle new allauth error format with errors array
          if (error?.response?.data?.errors && Array.isArray(error.response.data.errors)) {
            const firstError = error.response.data.errors[0];
            if (firstError?.message) {
              errorMessage = firstError.message;
            }
          }
          // Handle old format with detail field
          else if (error?.response?.data?.detail) {
            errorMessage = error.response.data.detail;
          }
          
          setLoginMessage(errorMessage);
          setAuthModalType("login");
          setShowAuthModal(true);
          navigate('/', { replace: true });
        }
      } finally {
        setLoading(false);
      }
    };

    handleEmailVerification();
  }, [searchParams, navigate, confirmEmail, setLoginMessage, setAuthModalType, setShowAuthModal, processed]);
  
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          p: 3
        }}
      >
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Verifying your email...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please wait while we confirm your email address.
        </Typography>
      </Box>
    );
  }

  return null;
}

ReactGA.initialize('G-CYZDLKYWRF');

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const router = createBrowserRouter([{
    element: (
      <ConfigLoader>
        <AuthRedirector />
        <Outlet />
      </ConfigLoader>
    ),
    children: [{
      path: "/",
      element:  <Root />,
      children: [{
          index: true,
          element: <Home />,
          errorElement: <RouteErrorPage />,
        },{
          path: "designs",
          element: <Suspense fallback={<PageLoading />}><Models /></Suspense>,
          errorElement: <RouteErrorPage />,
        },{
          path: "designs/:id",
          element: (
            <Suspense fallback={<ModelPageSkeleton />}>
              <ModelProvider>
                <Model />
              </ModelProvider>
            </Suspense>
          ),
          errorElement: <RouteErrorPage />,
        },{
          // Redirects for backward compatibility
          path: "models",
          element: <Navigate to="/designs" replace />,
        },{
          path: "model/:id",
          element: <ModelRedirect />,
        },{
          path: "library",
          element: (
            <Suspense fallback={<PlansPageSkeleton />}>
              <RequireAuth>
                <Library />
              </RequireAuth>
            </Suspense>
          ),
          errorElement: <RouteErrorPage />,
        },{
          path: "plans",
          element: <Navigate to="/library?tab=plan" replace />,
        },{
          path: "usermodels",
          element: <Navigate to="/library?tab=design" replace />,
        },{
          path: "settings",
          element: (
            <Suspense fallback={<PageLoading />}>
              <RequireAuth>
                <UserSetting />
              </RequireAuth>
            </Suspense>
          ),
          errorElement: <RouteErrorPage />,
        },{
          path: "contact",
          element: <Suspense fallback={<PageLoading />}><Contact /></Suspense>,
          errorElement: <RouteErrorPage />,
        },{
          path: "faq",
          element: <Suspense fallback={<PageLoading />}><FAQ /></Suspense>,
          errorElement: <RouteErrorPage />,
        },{
          path: "auth/callback",
          element: <Suspense fallback={<PageLoading />}><OAuthCallback /></Suspense>,
          errorElement: <RouteErrorPage />,
        },{
          path: "password-reset/:uid/:token",
          element: <PasswordResetHandler />,
          errorElement: <RouteErrorPage />,
        },{
          path: "login",
          element: <LoginHandler />,
          errorElement: <RouteErrorPage />,
        },{
          path: "signup",
          element: <SignupHandler />,
          errorElement: <RouteErrorPage />,
        },{
          path: "password-reset",
          element: <PasswordResetFormHandler />,
          errorElement: <RouteErrorPage />,
        },{
          path: "email-verification",
          element: <EmailVerificationHandler />,
          errorElement: <RouteErrorPage />,
        },{
          path: "email-confirmed",
          element: <EmailConfirmedHandler />,
          errorElement: <RouteErrorPage />,
        },{
          path: "privacy-policy",
          element: <Suspense fallback={<PageLoading />}><PrivacyPolicy /></Suspense>,
          errorElement: <RouteErrorPage />,
        },{
          path: "data-deletion",
          element: <Suspense fallback={<PageLoading />}><DataDeletion /></Suspense>,
          errorElement: <RouteErrorPage />,
        },{
          path: "checkout/success",
          element: <Suspense fallback={<PageLoading />}><CheckoutSuccess /></Suspense>,
          errorElement: <RouteErrorPage />,
        },{
          path: "checkout/cancel",
          element: <Suspense fallback={<PageLoading />}><CheckoutCancel /></Suspense>,
          errorElement: <RouteErrorPage />,
        },
        {
          path: "*",
          element: <NotFound />
        }]
      }]
    }]
  );
  
function AppProviders({ children }) {
  return (
      <HelmetProvider>
        <GlobalProvider>
          <AuthStateProvider>
            <ApiProvider>
              <AuthOperationsProvider>
                <ConfigProvider>
                  <OnboardingProvider>
                    <CombinedAuthProvider>
                      { children }
                    </CombinedAuthProvider>
                  </OnboardingProvider>
                </ConfigProvider>
              </AuthOperationsProvider>
            </ApiProvider>
          </AuthStateProvider>
        </GlobalProvider>
      </HelmetProvider>
  );
}

const root = ReactDOM.createRoot(
  document.getElementById('root')
);
root.render(
    <React.StrictMode>  
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
          <AppTheme>
            <AppProviders>
              <RouterProvider router={router}/>
            </AppProviders>
          </AppTheme>
        </GoogleOAuthProvider>
    </React.StrictMode>
);

// root.render(
//   <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
//     <AppTheme>
//       <AppProviders>
//         <RouterProvider router={router}/>
//       </AppProviders>
//     </AppTheme>
//   </GoogleOAuthProvider>
// );

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
